import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { EMPTY, Observable, take, timer } from "rxjs";
import { switchMap } from "rxjs/operators";

import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { HaModalService } from "@ha/ui/common";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { WheelDatetimePickerModalComponent } from "../../../shared/components/wheel-datetime-picker/wheel-datetime-picker-modal.component";
import { WorkplaceDetails } from "../../models/workplace-details.model";
import { PermitCreateActions } from "../../states/permit-create.actions";
import { PermitCreateSelectors } from "../../states/permit-create.selectors";

const permitStatusRefreshIntervalMs = 15000;

@UntilDestroy()
@Component({
    selector: "pmt-valid-time",
    templateUrl: "./valid-time.component.html",
    styleUrls: ["./valid-time.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidTimeComponent implements OnInit, OnChanges {
    @Input()
    public default: number;
    @Input()
    public comment: string;
    @Input()
    public permitDuration: number;

    @Select(PermitCreateSelectors.isPremium)
    public isPremium$: Observable<boolean>;

    @Select(PermitCreateSelectors.validDateTime)
    public validDateTime$: Observable<string>;

    @Select(PermitCreateSelectors.workplaceDetails)
    public workplaceDetails$: Observable<WorkplaceDetails>;

    @Select(PermitCreateSelectors.canStartPermit)
    public canStartPermit$: Observable<boolean>;

    @Select(PermitCreateSelectors.startPermitErrorMessage)
    public startPermitErrorMessage$: Observable<string | undefined>;

    public validDateTime: string;
    public canProceed = true;
    public startPermitAction = PermitCreateActions.StartPermit;
    public form: UntypedFormGroup;

    constructor(
        private modalService: HaModalService,
        private fb: UntypedFormBuilder,
        private store: Store,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.default && !changes.default.firstChange) {
            const newDateTime = new Date(new Date().setHours(new Date().getHours() + this.default)).toJSON();
            this.store.dispatch(new PermitCreateActions.SetValidDateTime(newDateTime));
        }
    }

    public ngOnInit(): void {
        this.form = this.fb.group({
            validDateTime: [{ value: "", disabled: true }, Validators.required],
        });

        // Set initial validDateTime based on default hours
        const initialDateTime = new Date(new Date().setHours(new Date().getHours() + this.default)).toJSON();
        this.store.dispatch(new PermitCreateActions.SetValidDateTime(initialDateTime));

        this.validDateTime$.pipe(untilDestroyed(this)).subscribe((dateTime) => {
            this.validDateTime = dateTime;
            this.form.get("validDateTime").setValue(dateTime);
            this.cdr.detectChanges();
        });

        this.actions$.pipe(ofActionSuccessful(PermitCreateActions.StartPermit)).pipe(untilDestroyed(this), take(1))
            .subscribe(() => {
                this.store.dispatch(new PermitStepActions.GetCurrentStep());
            });

        this.actions$.pipe(ofActionSuccessful(PermitCreateActions.SetValidDateTime)).pipe(untilDestroyed(this))
            .subscribe(() => {
                const validDateTime = this.store.selectSnapshot(PermitCreateSelectors.validDateTime);
                const validDate = new Date(validDateTime);
                const now = new Date();
                const diffInHours = Math.floor((validDate.getTime() - now.getTime()) / (1000 * 60 * 60));

                this.canProceed = this.store.selectSnapshot(PermitCreateSelectors.isPremium)
                    ? diffInHours <= this.permitDuration
                    : true;

                this.cdr.detectChanges();
            });

        this.canStartPermit$.pipe(
            untilDestroyed(this),
            switchMap((canStartPermit) => !canStartPermit ? timer(0, permitStatusRefreshIntervalMs) : EMPTY),
        ).subscribe(() => {
            this.refreshPermitStatus();
        });
    }

    public showUpdateTimeModal(): void {
        const isPremium = this.store.selectSnapshot(PermitCreateSelectors.isPremium);
        const premiumPermitDuration = this.store.selectSnapshot(PermitCreateSelectors.workplaceDetails)?.permitDuration;
        this.modalService.open(WheelDatetimePickerModalComponent, true, {
            default: this.validDateTime,
            maxValue: isPremium ? premiumPermitDuration : 168,
        });
    }

    public start(): void {
        const canStartPermit = this.store.selectSnapshot(PermitCreateSelectors.canStartPermit);

        if (!this.canProceed || !canStartPermit) {
            return;
        }

        this.store.dispatch(new PermitCreateActions.StartPermit(this.validDateTime));
    }

    public goBack(): void {
        this.store.dispatch(new PermitStepActions.GoBackwards());
    }

    private refreshPermitStatus(): void {
        const canStartPermit = this.store.selectSnapshot(PermitCreateSelectors.canStartPermit);
        const permitId = this.store.selectSnapshot(PermitCreateSelectors.permitId);

        if (canStartPermit || !permitId) {
            return;
        }

        this.store.dispatch(new PermitCreateActions.GetPermit(permitId));
    }
}
